<template>
	<o-sticker
		class="candidate-card-client"
		:selected="selected"
		@click="setup.openCandidateProfile()">
		<div class="candidate-card-client__content">
			<div class="candidate-card-client__header">
				<div class="candidate-card-client__header-content">
					<p class="candidate-card-client__user-name">
						{{ orderClientProposal.tempWorkerUser?.firstName }}
						{{ orderClientProposal.tempWorkerUser?.lastName ? orderClientProposal.tempWorkerUser?.lastName[0] : '' }}.
					</p>
					<p
						v-if="setup.tempWorkerAddress && orderClientProposal.tempWorkerUser?.profileIsPublic"
						class="candidate-card-client__user-city">
						{{ setup.tempWorkerAddress }}
					</p>
				</div>
			</div>
			<div v-if="!validated">
				<div class="candidate-card-client__main">
					<div class="candidate-card-client__icon-section">
						<o-alert
							v-if="!orderClientProposal.tempWorkerUser?.profileIsPublic"
							color="warning">
							{{ $t('business.order.tempWorkerDoesNotShareProfile') }}
						</o-alert>
						<div
							v-else
							:ref="setup._skills"
							class="candidate-card-client__chips-section">
							<o-chips
								v-for="(skill, index) in setup.skills"
								:key="index"
								outline
								class="candidate-card-client__chips">
								{{ skill.label }}
							</o-chips>
							<div
								v-if="!setup.skills?.length"
								class="candidate-card-client__empty">
								{{ $t('business.order.noQualificationProvided') }}
							</div>
						</div>
					</div>
				</div>
			</div>

			<div v-else>
				<o-icon
					class="candidate-card-client__validated-icon"
					font-icon="icon-interface-validation-check"/>
			</div>
		</div>
		<template
			v-if="!validated"
			#actions>
			<o-label
				:color="setup.proposalStatus?.color"
				outline>
				{{ setup.proposalStatus?.label }}
			</o-label>
			<o-icon
				v-tooltip="$t('business.order.viewProfile')"
				font-icon="icon-image-picture-portrait-view"
				marker-color="info"
				:marker="orderClientProposal.tempWorkerUser?.userProfileCommentsCount"
				ripple="info"
				@click="setup.openCandidateProfile()"/>
			<template v-if="orderClientProposal.status === ORDER_PROPOSAL_STATUS.Waiting">
				<o-icon
					v-tooltip="$t('business.order.acceptCandidate')"
					font-icon="icon-interface-user-check"
					ripple="success"
					@click="setup.acceptCandidateAsync()"/>
				<o-icon
					v-tooltip="$t('business.order.declineCandidate')"
					font-icon="icon-interface-user-delete"
					ripple="danger"
					@click="setup.declineCandidateAsync()"/>
				<o-checkbox
					v-if="!setup.isTouch"
					:model-value="selected"
					class="proposal-checkbox"
					@click.stop="emits('select')"/>
			</template>
		</template>
	</o-sticker>
</template>

<script setup lang="ts">
import OrderEntity from '@/entity/order/OrderEntity';
import { ORDER_PROPOSAL_STATUS } from '@/typings/enums/ORDER_PROPOSAL_STATUS';
import { inject } from 'vue';
const orderEntity = inject<OrderEntity>('OrderEntity');
// #region Setup
import type { OrderClientProposalCardSetupEmits, OrderClientProposalCardSetupProps } from './OrderClientProposalCardSetup';
import OrderClientProposalCardSetup from './OrderClientProposalCardSetup';
const emits = defineEmits<OrderClientProposalCardSetupEmits>() as OrderClientProposalCardSetupEmits;
const props = withDefaults(defineProps<OrderClientProposalCardSetupProps>(), OrderClientProposalCardSetup.defaultProps);
const setup = new OrderClientProposalCardSetup(props, emits, orderEntity);
// #endregion
</script>

<style lang="less" scoped>
@import "@/styles/variables";
@import "@/styles/mixins";

.candidate-card-client {
	position: relative;

	p {
		margin: unset;
	}

	&__header {
		position: relative;
		display: flex;
		flex: 0 0 12rem;
		gap: 0.9rem;
		align-items: center;
	}

	&__user-name {
		font-size: 0.9rem;
		font-weight: 600;
		color: var(--grey-darker);
	}

	&__user-city {
		font-size: 0.9rem;
		color: var(--grey-dark);
	}

	&__icon-section {
		display: flex;

		.orion-icon {
			margin-right: 0.94rem;
			font-size: 1.1rem;
			color: var(--grey);
		}
	}

	&__chips-section {
		overflow: hidden;
		display: flex;
		flex: 1;
		flex-wrap: wrap;
		gap: 0.4rem;
	}

	&__status {
		position: absolute;
		z-index: 2;
		bottom: 0.6rem;
		left: 0;
		display: flex;
		justify-content: center;
		width: 100%;
		padding: 1.3rem;
		border-radius: 0.6rem;
		transition: all 0.2s ease;

		body.istouch & {
			position: relative;
			bottom: auto;
			padding-bottom: 0;
		}
	}

	:deep(.proposal-checkbox) {
		position: absolute;
		right: -2.5rem;
	}

	&__validated-icon {
		position: absolute;
		top: 1rem;
		right: 1rem;
		padding: 0.5rem;
		border-radius: 50%;
		font-size: 0.6rem;
		color: var(--success);
		background: rgba(var(--rgb-success), 0.1);
	}

	&__content {
		display: flex;
		align-items: center;
	}

	&__header-content {
		display: flex;
		flex-direction: column;
		gap: 0.25rem;
	}

	&__avatar {
		position: relative;
		overflow: hidden;
		display: flex;
		flex-direction: column;
		align-items: center;
		width: 2.5rem;
		height: 2.5rem;
		border-radius: 50%;
		background: var(--grey-lighter);

		&::after,
		&::before {
			content: "";
			position: absolute;
			border-radius: 50%;
			background: var(--grey-light);
		}

		&::before {
			top: 0.44rem;
			width: 0.9rem;
			height: 0.9rem;
		}

		&::after {
			bottom: -0.9rem;
			width: 1.9rem;
			height: 1.9rem;
		}
	}

	&__bubble {
		position: absolute;
		top: 0;
		right: 0;
		display: flex;
		flex-direction: row-reverse;
		align-items: center;
	}

	&__bubble-icon {
		cursor: pointer;
		display: flex;
		align-items: center;
		justify-content: center;
		width: 3rem;
		height: 3rem;
		border-radius: 50%;
		background: var(--grey-lighter);

		.orion-icon {
			font-size: 1.3rem;
			color: var(--grey);
		}

		&:hover {
			border: 0.06rem solid var(--info);

			.orion-icon {
				color: var(--info);
			}
		}
	}

	&__bubble-counter {
		position: absolute;
		left: -0.8rem;
		display: flex;
		align-items: center;
		justify-content: center;
		width: 1.3rem;
		height: 1.3rem;
		border: 0.13rem solid var(--grey-lighter);
		border-radius: 50%;
		font-size: 0.6rem;
		background: white;
	}

	body:not(.istouch) &:not(.candidate-card-agency--allways-show-actions) {
		.orion-card__actions {
			> .orion-icon {
				transform: translate3d(30%, 0, 0);
				opacity: 0;
				transition: opacity 0.3s, transform 0.2s;
			}

			> .div-action {
				transform: translate3d(1.9rem, 0, 0);
				display: flex;
				align-items: center;
				height: 3rem;
				margin: -0.6rem 0.6rem;
				margin-left: 0.19rem;
				opacity: 0;
				transition: opacity 0.3s, transform 0.2s;

				> .separator {
					transform: translate3d(1.3rem, 0, 0);
					//transition: opacity 0.3s, transform 0.2s;
					display: block;
					width: 0.06rem;
					height: 3rem;
					opacity: 0;
					background:
						linear-gradient(
							to bottom,
							white 0%,
							var(--grey) 50%,
							white 100%
						);
				}
			}
		}
	}

	body:not(.istouch) & {
		&:hover {
			.candidate-card-client__status {
				transform: translate3d(0, -100%, 0);
				opacity: 0;
			}

			.orion-card__actions > .div-action > .separator {
				margin-right: 0.3rem;
			}

			.orion-card__actions > .orion-icon,
			.orion-card__actions > .div-action > .separator,
			.orion-card__actions > .div-action {
				transform: translate3d(0, 0, 0);
				opacity: 1;

				each(range(5), {
					&:nth-child(@{value}) {
						transition-delay: calc(@value * 0.07s);
					}
				});
			}
		}
	}
}
</style>
